import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogDismiss,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
} from '@barfacil/barfacil-ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Product } from '@/api/_schemas/management'
import { UpdateProductTaxForm } from '@/app/panel/management/price-list/_components/update-product-tax-form'
import { useUpdateProductContext } from '@/app/panel/management/price-list/_contexts/update-product-context'
import { usePriceListCategoryOptions } from '@/app/panel/management/price-list/_hooks/use-category-options'
import { useProductInfo } from '@/app/panel/management/price-list/_hooks/use-product-info'
import { useSyncTaxGroup } from '@/app/panel/management/price-list/_hooks/use-sync-tax-group'
import { useTaxList } from '@/app/panel/management/price-list/_hooks/use-tax-list'
import { useUpdateProduct } from '@/app/panel/management/price-list/_hooks/use-update-product'
import { useUpdateProductMapper } from '@/app/panel/management/price-list/_hooks/use-update-product-mapper'
import {
  FormUpdateProduct,
  FormUpdateProductSchema,
} from '@/app/panel/management/price-list/_schemas/update-product'
import { cn } from '@/lib/utils'

type UpdateProductTaxDialogProps = {
  product: Product
  isInactive?: boolean
}

export const UpdateProductTaxDialog = ({
  product,
  isInactive = false,
}: UpdateProductTaxDialogProps) => {
  const [open, setOpen] = useState(false)

  const { comboProducts } = useUpdateProductContext()
  const { mapEntityToForm, mapFormToPayload } = useUpdateProductMapper()
  const { mutation, formErrors } = useUpdateProduct()
  const { syncTax } = useSyncTaxGroup()

  const { data: productWithTax } = useProductInfo(
    { id: product.cod_produto },
    open,
  )
  const { isLoading: isLoadingCategoryOptions } = usePriceListCategoryOptions()
  const { isLoading: cfopListLoading } = useTaxList('cfopList', open)
  const { isLoading: ncmListLoading } = useTaxList('ncmList', open)
  const { isLoading: icmsOrigemListLoading } = useTaxList(
    'icmsOrigemList',
    open,
  )
  const { isLoading: icmsSituacaoListLoading } = useTaxList(
    'icmsSituacaoList',
    open,
  )
  const { isLoading: icmsModalidadeListLoading } = useTaxList(
    'icmsModalidadeList',
    open,
  )
  const { isLoading: pisCofinsTipoListLoading } = useTaxList(
    'pisCofinsTipoList',
    open,
  )
  const { isLoading: pisCofinsSituacaoListLoading } = useTaxList(
    'pisCofinsSituacaoList',
    open,
  )

  const isLoading =
    mutation.isPending ||
    isLoadingCategoryOptions ||
    cfopListLoading ||
    ncmListLoading ||
    icmsOrigemListLoading ||
    icmsSituacaoListLoading ||
    icmsModalidadeListLoading ||
    pisCofinsTipoListLoading ||
    pisCofinsSituacaoListLoading

  const form = useForm<FormUpdateProduct>({
    resolver: zodResolver(FormUpdateProductSchema),
    defaultValues: mapEntityToForm(product),
  })

  function handleOnOpenChange(open: boolean) {
    if (open) {
      form.reset(mapEntityToForm(product))
    }

    setOpen(open)
  }

  function handleSubmit<T>(e: React.FormEvent<T>) {
    e.preventDefault()

    return form.handleSubmit(async (data) => {
      await mutation.mutateAsync(mapFormToPayload(data, comboProducts), {
        onSuccess: () => setOpen(false),
      })
    })()
  }

  useEffect(() => {
    form.setValue(
      'pis_situacao_tributaria',
      productWithTax?.pis_situacao_tributaria ?? '',
    )

    form.setValue(
      'cofins_situacao_tributaria',
      productWithTax?.cofins_situacao_tributaria ?? '',
    )

    form.setValue(
      'grupo_tributario',
      productWithTax?.grupo_tributario?.cod_grupo_tributario?.toString(),
    )

    syncTax(form, productWithTax?.grupo_tributario)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productWithTax, open])

  useEffect(() => {
    if (!formErrors.length) return

    formErrors.forEach(({ key, messages }) => {
      form.setError(key as keyof FormUpdateProduct, {
        message: messages[0],
      })
    })
  }, [form, formErrors])

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogTrigger asChild>
        <Button
          className={cn(
            'h-8 w-14 p-0',
            product.emitir_nfce &&
              !isInactive &&
              'bg-green-500 hover:bg-green-400 active:bg-green-800',
          )}
          variant="danger"
          size="sm"
          disabled={isInactive}
        >
          NFC-e
        </Button>
      </DialogTrigger>

      <DialogContent className="w-full max-w-5xl gap-0 sm:h-[600px] 2xl:h-[700px]">
        <DialogHeader className="mb-8">
          <DialogTitle className="leading-normal max-sm:pr-10">
            ALTERAR PRODUTO {product.nome}
          </DialogTitle>
        </DialogHeader>

        <DialogDescription className="mb-2 text-blue-500">
          INFORMAÇÕES FISCAIS
        </DialogDescription>

        <DialogBody className="overflow-auto p-1">
          <Form {...form}>
            <form id="update-product-tax-form" onSubmit={handleSubmit} />
            <UpdateProductTaxForm />
          </Form>
        </DialogBody>

        <DialogFooter className="mt-4 max-sm:gap-2">
          <Button
            className="sm:w-32"
            type="submit"
            form="update-product-tax-form"
            disabled={isLoading}
            isLoading={mutation.isPending}
          >
            SALVAR
          </Button>

          <DialogClose asChild>
            <Button className="sm:w-32" variant="tertiary" type="button">
              CANCELAR
            </Button>
          </DialogClose>
        </DialogFooter>

        <DialogDismiss className="absolute right-6 top-6" />
      </DialogContent>
    </Dialog>
  )
}
